import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography
} from '@material-ui/core';
import { ArrowRight, ChevronDown, TrendingDown } from 'lucide-react';
import React from 'react';
import {
  AssistantBubble,
  AssistantBubbleRow,
  ChatBody,
  ChatExchange,
  ChatHeader,
  ChatHeaderName,
  ChatHeaderStatus,
  ChatWindow,
  ClientAvatar,
  ClientLogo,
  ReportChip,
  UserBubble,
  UserBubbleRow
} from '../components/ChatMockup';
import { DemoButton } from '../components/DemoButton';
import { Headline } from '../components/Headline';
import { mobileHeroLeadText } from '../components/MarketingTypography';
import { GradientBackground } from '../components/HeroSection/GradientBackground';
import { Kicker } from '../components/Kicker';
import { Reveal } from '../components/motion';
import { EarlyAccessBadge } from '../components/NewBadge';
import { SEO } from '../components/SEO';
import { DEMO_CAMPAIGNS } from '../constants/demo-copy';
import MainLayout from '../layouts/main';
import styled from '../styled';
import { COLORS } from '../themes/color';

/* ------------------------------ Hero ------------------------------ */

const HeroPanel = styled('section')`
  position: relative;
  overflow: hidden;
  padding: 128px 24px 72px;

  @media (max-width: 800px) {
    padding: 96px 24px 48px;
  }
`;

const HeroInner = styled('div')`
  position: relative;
  z-index: 1;
  max-width: 1100px;
  margin: 0 auto;
`;

const HeroGrid = styled('div')`
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  gap: 48px;
  align-items: center;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: 36px;
  }
`;

const HeroCopy = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const HeroDescription = styled('p')`
  font-size: 18px;
  line-height: 1.65;
  color: ${COLORS.TEXT_SECONDARY};
  max-width: 560px;
  margin: 20px 0 32px;
  ${mobileHeroLeadText}
`;

const HeroBadgeRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

/* --------------------------- Use cases ---------------------------- */

const WorkflowRow = styled('div')<{ flip?: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 56px;
  align-items: center;
  padding: 40px 0;
  direction: ${(p) => (p.flip ? 'rtl' : 'ltr')};

  > * {
    direction: ltr;
  }

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: 28px;
    direction: ltr;
    padding: 28px 0;
  }
`;

const WorkflowContent = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 14px;
  align-items: flex-start;
`;

const WorkflowBody = styled('p')`
  font-size: 16px;
  line-height: 1.65;
  color: ${COLORS.TEXT_SECONDARY};
  margin: 0;
  max-width: 480px;
`;

const LayerChips = styled('div')`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const LayerChip = styled('span')`
  font-size: 12px;
  font-weight: 600;
  color: ${COLORS.PRIMARY};
  background: ${COLORS.PRIMARY_SURFACE};
  border: 1px solid ${COLORS.PRIMARY_BORDER};
  border-radius: 100px;
  padding: 4px 12px;
`;

const UseCaseGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-top: 16px;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

const MiniUseCaseCard = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const MiniUseCaseTitle = styled('h3')`
  font-size: 16px;
  font-weight: 700;
  color: ${COLORS.INK};
  margin: 0;
  line-height: 1.4;
`;

const RateChartBody = styled('div')`
  padding: 16px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 28px;
  height: 96px;
`;

const RateChartColumn = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  height: 100%;
  justify-content: flex-end;
`;

const RateChartBar = styled('div')<{ height: number; muted?: boolean }>`
  width: 34px;
  height: ${(p) => p.height}%;
  border-radius: 4px 4px 0 0;
  background: ${(p) => (p.muted ? COLORS.GOLD.gold6 : COLORS.PRIMARY)};
`;

const RateChartLabel = styled('div')`
  font-size: 11px;
  color: ${COLORS.GRAY.muted};
  text-align: center;
  line-height: 1.4;

  strong {
    display: block;
    font-size: 12.5px;
    color: ${COLORS.INK};
  }
`;

const AttachmentCard = styled('div')`
  margin-top: 10px;
  border-radius: 12px;
  overflow: hidden;
  background: ${COLORS.WHITE};
  border: 1px solid ${COLORS.GRAY.border};
`;

const AttachmentHeader = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 12px;
  background: ${COLORS.GRAY.borderLight};
  font-size: 12px;
  font-weight: 600;
  color: ${COLORS.GRAY.slate};
`;

type McpClient = { name: string; logo: string };

type WorkflowUseCase = {
  eyebrow: string;
  title: string;
  body: string;
  client: McpClient;
  prompt: string;
  layers: string[];
  response: string;
  reportLabel: string;
  chart?: {
    bars: { label: string; value: string; height: number; muted?: boolean }[];
  };
};

type MiniUseCase = {
  title: string;
  client: McpClient;
  prompt: string;
  response: string;
  reportLabel: string;
};

const renderMiniChat = (useCase: {
  client: McpClient;
  prompt: string;
  response: string;
  reportLabel: string;
}) => (
  <ChatWindow $compact aria-hidden>
    <ChatHeader>
      <ClientAvatar>
        <ClientLogo src={useCase.client.logo} alt="" />
      </ClientAvatar>
      <ChatHeaderName>{useCase.client.name}</ChatHeaderName>
      <ChatHeaderStatus>Connected</ChatHeaderStatus>
    </ChatHeader>
    <ChatBody $compact>
      <ChatExchange
        client={useCase.client}
        prompt={useCase.prompt}
        compact
        response={
          <>
            {useCase.response}
            <br />
            <ReportChip>
              {useCase.reportLabel} <ArrowRight size={12} />
            </ReportChip>
          </>
        }
      />
    </ChatBody>
  </ChatWindow>
);

/* ---------------------------- Sections ---------------------------- */

const Section = styled('section')`
  max-width: 1100px;
  margin: 0 auto;
  padding: 88px 24px 0;

  @media (max-width: 800px) {
    padding-top: 64px;
  }
`;

const SectionHeader = styled('div')`
  text-align: center;
  max-width: 720px;
  margin: 0 auto 48px;
`;

const SectionDescription = styled('p')`
  font-size: 17px;
  line-height: 1.65;
  color: ${COLORS.GRAY.muted};
  margin: 16px 0 0;
`;

/* --------------------------- Benefits ----------------------------- */

const BenefitsGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const BenefitCard = styled('div')`
  padding: 24px 22px;
  background: ${COLORS.WHITE};
  border: 1px solid ${COLORS.GRAY.border};
  border-radius: 14px;
  box-shadow: 0 4px 20px rgba(14, 28, 43, 0.04);
`;

const BenefitTitle = styled('h3')`
  font-size: 17px;
  font-weight: 700;
  color: ${COLORS.INK};
  margin: 0 0 8px;
`;

const BenefitBody = styled('p')`
  font-size: 15px;
  line-height: 1.6;
  color: ${COLORS.GRAY.muted};
  margin: 0;
`;

/* ----------------------------- Clients ---------------------------- */

const ClientMarquee = styled('div')`
  overflow: hidden;
  mask-image: linear-gradient(
    to right,
    transparent,
    black 8%,
    black 92%,
    transparent
  );
  -webkit-mask-image: linear-gradient(
    to right,
    transparent,
    black 8%,
    black 92%,
    transparent
  );

  @keyframes mcpClientMarquee {
    from {
      transform: translateX(0);
    }
    to {
      transform: translateX(-50%);
    }
  }
`;

const ClientTrack = styled('div')`
  display: flex;
  gap: 12px;
  width: fit-content;
  animation: mcpClientMarquee 40s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    flex-wrap: wrap;
    justify-content: center;
    width: 100%;
  }
`;

const ClientChip = styled('span')`
  font-size: 14px;
  font-weight: 600;
  color: ${COLORS.INK};
  background: ${COLORS.WHITE};
  border: 1px solid ${COLORS.GRAY.border};
  border-radius: 100px;
  padding: 10px 18px;
  white-space: nowrap;
  box-shadow: 0 4px 16px rgba(14, 28, 43, 0.04);
`;

/* ------------------------------ Steps ----------------------------- */

const StepsGrid = styled('div')`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  align-items: stretch;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const StepCard = styled('div')`
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  padding: 28px 24px;
  background: ${COLORS.WHITE};
  border: 1px solid ${COLORS.GRAY.border};
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(14, 28, 43, 0.04);
`;

const StepNumber = styled('div')`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${COLORS.PRIMARY};
  margin-bottom: 12px;
`;

const StepTitle = styled('h3')`
  font-size: 20px;
  font-weight: 700;
  color: ${COLORS.INK};
  margin: 0 0 10px;
`;

const StepBody = styled('p')`
  font-size: 15px;
  line-height: 1.65;
  color: ${COLORS.GRAY.muted};
  margin: 0;
`;

/* ------------------------------- FAQ ------------------------------ */

const FaqSection = styled('div')`
  max-width: 760px;
  margin: 0 auto;
`;

const FaqQuestion = styled('h3')`
  font-size: 16px;
  font-weight: 600;
  margin: 0;
`;

const FinalCta = styled('div')`
  text-align: center;
  max-width: 720px;
  margin: 88px auto 120px;
  padding: 0 24px;
`;

/* ------------------------------ Data ------------------------------ */

const MCP_CLIENTS = [
  'Claude',
  'Codex',
  'ChatGPT',
  'Cursor',
  'Copilot',
  'Gemini',
  'VS Code',
  'Windsurf'
];

const MCP_BENEFITS = [
  {
    title: 'Connect once from Settings',
    body:
      'OAuth setup in minutes. No API keys to copy, and the connection respects your workspace permissions.'
  },
  {
    title: 'Answers link back to Affilimate',
    body:
      'When you need to verify a number, jump straight to the report behind the answer in Affilimate.'
  },
  {
    title: 'Works with your other tools',
    body:
      'Use Affilimate MCP alongside other connectors in Claude, ChatGPT, and compatible assistants.'
  }
] as const;

const WORKFLOW_USE_CASES: WorkflowUseCase[] = [
  {
    eyebrow: 'Protect revenue',
    title: 'Find out-of-stock products on your top pages',
    body:
      'Affilimate matches the product links on your pages against 70M+ tracked products, aligned to what you publish and earn at portfolio scale. Ask once and see which pages are losing revenue right now.',
    client: { name: 'Claude', logo: '/images/logos/claude-mcp.svg' },
    prompt: 'Which of our top pages link to out-of-stock products?',
    layers: ['Tracked products', 'Page performance', 'Commissions'],
    response:
      '3 of your highest-traffic pages link to products that are now out of stock, putting about 41K pageviews at risk. Your espresso machines guide has the most affected links. Nine in-stock alternatives are already matched to your catalog.',
    reportLabel: 'Open the content report'
  },
  {
    eyebrow: 'Plan and negotiate',
    title: 'Model a rate change before it hits your revenue',
    body:
      'Commission rates and transactions from 100+ networks sit next to your page data. Ask what a rate cut would cost based on what you actually earned, and see which pages carry the exposure.',
    client: { name: 'ChatGPT', logo: '/images/logos/chatgpt-mcp.svg' },
    prompt:
      'Forecast our revenue if our top merchant cuts commission rates from 10% to 8%.',
    layers: ['Commissions', 'Rates', 'Page performance'],
    response:
      'If your top merchant cuts rates from 10% to 8%, you would lose about $12.4K a month based on your last 90 days of transactions. That is concentrated in just 14 pages, and 3 alternate merchants carry the same products at 9 to 12%.',
    reportLabel: 'Open the rates report',
    chart: {
      bars: [
        { label: 'Current', value: '$62.0K/mo', height: 100 },
        { label: 'At 8%', value: '$49.6K/mo', height: 80, muted: true }
      ]
    }
  }
];

const MINI_USE_CASES: MiniUseCase[] = [
  {
    title: 'Break down earnings by author and section',
    client: { name: 'Claude', logo: '/images/logos/claude-mcp.svg' },
    prompt: 'Break down Q2 earnings by author and flag declining sections.',
    response:
      'Outdoor gear is down 18% quarter over quarter, led by two authors whose conversion fell on pages that still rank well. Finance and home are up 9% and 14%.',
    reportLabel: 'Open author report'
  },
  {
    title: 'Flag merchants trending down before payout',
    client: { name: 'ChatGPT', logo: '/images/logos/chatgpt-mcp.svg' },
    prompt: 'Which merchants declined this month, and why?',
    response:
      '4 merchants are down more than 15%. Two are volume-driven on pages you updated last month. One is a rate change on your top home category.',
    reportLabel: 'Open merchant report'
  },
  {
    title: 'Catch revenue lost to broken links',
    client: { name: 'Copilot', logo: '/images/logos/copilot-mcp.svg' },
    prompt: 'Which pages are losing revenue to broken or redirected links?',
    response:
      '12 pages have broken affiliate links earning nothing this month. The top three by lost revenue are ordered in the fix list.',
    reportLabel: 'Open link health report'
  },
  {
    title: 'Compare merchant rates before a negotiation',
    client: { name: 'ChatGPT', logo: '/images/logos/chatgpt-mcp.svg' },
    prompt:
      'How do our outdoor gear commission rates compare across top merchants?',
    response:
      'Your weighted average is 8.4%. Three merchants in the category pay 10 to 12% on the same products you already link to.',
    reportLabel: 'Open rates report'
  }
];

const faqs = [
  {
    question: 'What is the Affilimate MCP server?',
    answer:
      'A secure connection between your AI tools and your Affilimate workspace. Your commissions, page performance, catalog-matched products, and custom dimensions stay in one system, so questions can draw on the same commerce data as the rest of the platform.'
  },
  {
    question: 'What can I do with it?',
    answer:
      'Ask questions that need the full picture: find out-of-stock products on your top pages, model what a rate change would cost, compare author or section performance, and check merchant rates. Answers link back to live reports when you need to verify the numbers.'
  },
  {
    question: 'Which AI tools does it work with?',
    answer:
      "We've tested it most extensively with Claude and Codex, but since MCP is an open standard, it also works with hundreds of other compatible tools, including ChatGPT, Gemini, and Copilot. If yours supports remote MCP with OAuth, give it a try."
  },
  {
    question:
      'Can I combine it with other MCP servers like Ahrefs or Google Analytics?',
    answer:
      'Yes. Your AI assistant can consult several MCP servers in the same conversation. Connect Ahrefs, Semrush, or Google Analytics alongside Affilimate and ask questions that cross both datasets, like whether a revenue drop lines up with a ranking change. Results can also go to tools your team already uses, such as Slack, Google Sheets, Asana, and Monday.com.'
  },
  {
    question: 'How do I set it up?',
    answer:
      'Sign in to Affilimate, choose one workspace, and approve access. Most teams connect in under five minutes, no engineering work required.'
  },
  {
    question: 'What do I need to get started?',
    answer:
      'An Affilimate account with MCP enabled for your workspace and an AI client that supports MCP. There are no API keys to manage.'
  }
];

export default () => (
  <MainLayout>
    <SEO
      pathname="/mcp/"
      title="Affilimate MCP: Affiliate Data for Claude, ChatGPT & AI Tools"
      description="Connect Affilimate to Claude, ChatGPT, and other AI tools with MCP. Ask about earnings, campaigns, and page performance from the assistants your team already uses."
    />

    <HeroPanel>
      <GradientBackground />
      <HeroInner>
        <HeroGrid>
          <HeroCopy>
            <HeroBadgeRow
              style={{ justifyContent: 'flex-start', marginBottom: 16 }}
            >
              <EarlyAccessBadge />
            </HeroBadgeRow>
            <Headline component="h1" variant="h1">
              Your commerce workflows in AI assistants you already use
            </Headline>
            <HeroDescription>
              Affilimate MCP is included with the platform. Connect once, then
              ask about earnings, campaigns, pages, and merchants from AI
              assistants like Claude, ChatGPT, and Copilot.
            </HeroDescription>
            <DemoButton text="Get a demo" variant="contained" />
          </HeroCopy>

          <Reveal delay={120}>
            <ChatWindow aria-hidden>
              <ChatHeader>
                <ClientAvatar>
                  <ClientLogo src="/images/logos/claude-mcp.svg" alt="" />
                </ClientAvatar>
                <ChatHeaderName>Claude</ChatHeaderName>
                <ChatHeaderStatus>Connected</ChatHeaderStatus>
              </ChatHeader>
              <ChatBody>
                <UserBubbleRow>
                  <UserBubble>
                    Which sponsorships are behind pace this quarter?
                  </UserBubble>
                </UserBubbleRow>
                <AssistantBubbleRow>
                  <ClientAvatar>
                    <ClientLogo src="/images/logos/claude-mcp.svg" alt="" />
                  </ClientAvatar>
                  <AssistantBubble>
                    2 campaigns are behind pace. {DEMO_CAMPAIGNS.homeRefresh} is
                    at 38% of goal with 6 days left in the flight.{' '}
                    {DEMO_CAMPAIGNS.springAudio} is pacing at 61%.
                    <br />
                    <ReportChip>
                      Open campaign report <ArrowRight size={12} />
                    </ReportChip>
                  </AssistantBubble>
                </AssistantBubbleRow>
              </ChatBody>
            </ChatWindow>
          </Reveal>
        </HeroGrid>
      </HeroInner>
    </HeroPanel>

    <Section>
      <SectionHeader>
        <Reveal>
          <Headline component="h2">
            Included with your Affilimate workspace
          </Headline>
          <SectionDescription>
            MCP ships with your Affilimate workspace. Your team gets the same
            commerce data in Affilimate and in the assistants they already use.
          </SectionDescription>
        </Reveal>
      </SectionHeader>
      <BenefitsGrid>
        {MCP_BENEFITS.map((benefit, index) => (
          <Reveal key={benefit.title} delay={index * 70}>
            <BenefitCard>
              <BenefitTitle>{benefit.title}</BenefitTitle>
              <BenefitBody>{benefit.body}</BenefitBody>
            </BenefitCard>
          </Reveal>
        ))}
      </BenefitsGrid>
    </Section>

    <Section>
      <SectionHeader>
        <Reveal>
          <Headline component="h2">What teams ask</Headline>
          <SectionDescription>
            Actual questions that cross commissions, page performance, catalog
            context, and your editorial taxonomy. Each answer can link back to
            the Affilimate report behind it.
          </SectionDescription>
        </Reveal>
      </SectionHeader>

      {WORKFLOW_USE_CASES.map((workflow, index) => (
        <WorkflowRow key={workflow.title} flip={index % 2 === 1}>
          <Reveal>
            <WorkflowContent>
              <Kicker style={{ marginBottom: 0 }}>{workflow.eyebrow}</Kicker>
              <Headline component="h3" variant="h4" style={{ margin: 0 }}>
                {workflow.title}
              </Headline>
              <WorkflowBody>{workflow.body}</WorkflowBody>
              <LayerChips>
                {workflow.layers.map((layer) => (
                  <LayerChip key={layer}>{layer}</LayerChip>
                ))}
              </LayerChips>
            </WorkflowContent>
          </Reveal>
          <Reveal delay={120}>
            <ChatWindow aria-hidden>
              <ChatHeader>
                <ClientAvatar>
                  <ClientLogo src={workflow.client.logo} alt="" />
                </ClientAvatar>
                <ChatHeaderName>{workflow.client.name}</ChatHeaderName>
                <ChatHeaderStatus>Connected</ChatHeaderStatus>
              </ChatHeader>
              <ChatBody>
                <UserBubbleRow>
                  <UserBubble>{workflow.prompt}</UserBubble>
                </UserBubbleRow>
                <AssistantBubbleRow>
                  <ClientAvatar>
                    <ClientLogo src={workflow.client.logo} alt="" />
                  </ClientAvatar>
                  <AssistantBubble>
                    {workflow.response}
                    {workflow.chart && (
                      <AttachmentCard>
                        <AttachmentHeader>
                          <TrendingDown size={13} />
                          Revenue impact
                        </AttachmentHeader>
                        <RateChartBody>
                          {workflow.chart.bars.map((bar) => (
                            <RateChartColumn key={bar.label}>
                              <RateChartBar
                                height={bar.height}
                                muted={bar.muted}
                              />
                              <RateChartLabel>
                                {bar.label}
                                <strong>{bar.value}</strong>
                              </RateChartLabel>
                            </RateChartColumn>
                          ))}
                        </RateChartBody>
                      </AttachmentCard>
                    )}
                    <br />
                    <ReportChip>
                      {workflow.reportLabel} <ArrowRight size={12} />
                    </ReportChip>
                  </AssistantBubble>
                </AssistantBubbleRow>
              </ChatBody>
            </ChatWindow>
          </Reveal>
        </WorkflowRow>
      ))}

      <UseCaseGrid>
        {MINI_USE_CASES.map((useCase, index) => (
          <Reveal key={useCase.title} delay={index * 60}>
            <MiniUseCaseCard>
              <MiniUseCaseTitle>{useCase.title}</MiniUseCaseTitle>
              {renderMiniChat(useCase)}
            </MiniUseCaseCard>
          </Reveal>
        ))}
      </UseCaseGrid>
    </Section>

    <Section>
      <SectionHeader>
        <Reveal>
          <Headline component="h2">One connection, all your AI tools</Headline>
          <SectionDescription>
            Connect once with OAuth. Any MCP-compatible client can reach the
            same data, governed by the same workspace permissions.
          </SectionDescription>
        </Reveal>
      </SectionHeader>
      <ClientMarquee aria-hidden>
        <ClientTrack>
          {[...MCP_CLIENTS, ...MCP_CLIENTS].map((client, index) => (
            <ClientChip key={`${client}-${index}`}>{client}</ClientChip>
          ))}
        </ClientTrack>
      </ClientMarquee>
    </Section>

    <Section>
      <SectionHeader>
        <Reveal>
          <Headline component="h2">Connect in three steps</Headline>
        </Reveal>
      </SectionHeader>
      <StepsGrid>
        <Reveal delay={0} style={{ height: '100%' }}>
          <StepCard>
            <StepNumber>Step 1</StepNumber>
            <StepTitle>Connect</StepTitle>
            <StepBody>
              Add Affilimate as a connector in your AI tool of choice. Our
              documentation walks you through how to connect in the most popular
              AI assistants.
            </StepBody>
          </StepCard>
        </Reveal>
        <Reveal delay={80} style={{ height: '100%' }}>
          <StepCard>
            <StepNumber>Step 2</StepNumber>
            <StepTitle>Authorize</StepTitle>
            <StepBody>
              Sign in to Affilimate and choose the one workspace this connector
              can access. OAuth handles the rest. There are no API keys to copy.
            </StepBody>
          </StepCard>
        </Reveal>
        <Reveal delay={160} style={{ height: '100%' }}>
          <StepCard>
            <StepNumber>Step 3</StepNumber>
            <StepTitle>Put it to work</StepTitle>
            <StepBody>
              Start with an actual question about earnings, campaigns, or page
              performance. Answers link back to live Affilimate reports so your
              team can verify.
            </StepBody>
          </StepCard>
        </Reveal>
      </StepsGrid>
    </Section>

    <Section style={{ paddingTop: 96 }}>
      <SectionHeader>
        <Reveal>
          <Headline component="h2">FAQs</Headline>
          <SectionDescription>
            Quick answers to the most common questions about Affilimate MCP.
          </SectionDescription>
        </Reveal>
      </SectionHeader>
      <FaqSection>
        {faqs.map((faq, index) => (
          <Accordion key={faq.question} defaultExpanded={index === 0}>
            <AccordionSummary expandIcon={<ChevronDown size={20} />}>
              <FaqQuestion>{faq.question}</FaqQuestion>
            </AccordionSummary>
            <AccordionDetails>
              <Typography
                variant="body2"
                style={{ color: COLORS.TEXT_SECONDARY }}
              >
                {faq.answer}
              </Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </FaqSection>
    </Section>

    <FinalCta>
      <Reveal>
        <Headline component="h2">See it on your own data</Headline>
        <SectionDescription style={{ marginBottom: 28 }}>
          Book a demo and see how MCP fits into the commerce data your team
          already runs on Affilimate.
        </SectionDescription>
        <DemoButton text="Get a demo" variant="contained" />
      </Reveal>
    </FinalCta>
  </MainLayout>
);
